// StateIntroductiveVideo

// 1. Third-party plugins and files
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

// 2. Components
import Spinner from "../../components/spinner/Spinner";

// 6. Services
import {
  fetchSasUrl,
  logout,
  uploadToAzureBlob,
} from "../../services/IndiaService";
import { getStorageItemLocal } from "../../services/storageServices";

// 9. Config
import { appConfig } from "../../config/appConfig";

// Asset Components
import frameadd from "../../assets/FrameAdd.svg";
import video1 from "../../assets/videogallery.svg";
import visiable from "../../assets/visiable.svg";
import delete1 from "../../assets/delete.svg";

// 13. Styles
import "../indiaPdf/IndiaPdf.scss";
import VideoViewDialog from "./VideoViewDialog";

type AdminEntry = {
  id: number;
  name: string;
  uploadBy: string | null;
  introvideoURL: string | null;
  isActive: boolean;
};

const StateIntroductiveVideo = () => {
  // useStates
  const [dataList, setDataList] = useState<AdminEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);

  const [videoPreview, setVideoPreview] = useState<string | null>(null);

  const [openVideoDialog1, setOpenVideoDialog1] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rowsPerPage = 39;

  // Constants
  const MAX_FILE_SIZE_MB = 100;
  const ALLOWED_TYPES = "video/mp4";
  //  new changes /////////////////////////////////////////////////////////////////

  const handleViewIntroVideo = async (index: number) => {
    const firstItem = dataList.find((item) => item.id === index);
    //introvideoURL
    const videoPath = firstItem?.introvideoURL?.trim() ?? null;
    setVideoPreview(videoPath);
    setOpenVideoDialog1(true);
  };
  const handleDelete = async (index: number) => {
    if (!window.confirm("Are you sure want to delete this video?")) return;
    if (!dataList || !Array.isArray(dataList) || dataList.length === 0) {
      console.error("Error: dataList is empty");
      return;
    }
    setLoading(true);

    const selectedEntry = dataList.find((item) => item.id === index);
    if (!selectedEntry) {
      console.error("No entry found for id:", index);
      return;
    }
    const username = getStorageItemLocal("username") ?? "";
    const updatedEntry = {
      ...selectedEntry,
      isActive: selectedEntry.isActive,
      uploadBy: username,
      introvideoURL: null, // videoUrl,
    };

    const updatedList = dataList.map((item) =>
      item.id === index ? updatedEntry : item
    );
    setDataList(updatedList);
    const url = `${appConfig.serviceUrls.getActiveState}/${index}`;

    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedEntry),
      });

      if (!response.ok) throw new Error("Failed to save data");

      alert("Data saved successfully");
      fetchData(); // Refresh the data if needed
    } catch (error) {
      console.error("Error updating data:", error);
      alert("Failed to save data");
    } finally {
      setLoading(false);
    }
  };

  //   useEffect(() => {
  //     if (videoPath) {
  //       const extractedFileName = videoPath.split("/").pop()?.trim() ?? "";
  //       setFileName(extractedFileName);
  //     }

  //     setVideoPreview(firstItem?.subCategory ?? null);
  //   }, [dataList]);

  //////////////////////////////////////////////////////////////////

  // useNavigate
  const navigate = useNavigate();

  // useEffect
  useEffect(() => {
    fetchData();
  }, []);

  // Methods
  const fetchData = async () => {
    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      const response = await fetch(appConfig.serviceUrls.getActiveState, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 401) {
        logout(navigate);

        return;
      }

      if (!response.ok) throw new Error("Failed to fetch data");
      const data: AdminEntry[] = await response.json();
      setDataList(data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const uploadVideoToBlob = async (file: File) => {
    const tokenUrl = await fetchSasUrl("file-uploads"); // container name
    const pathPrefix = "StateVideos/intro"; // folder in blob
    const fileUrl = await uploadToAzureBlob(
      file,
      "file-uploads",
      tokenUrl,
      pathPrefix
    );

    return fileUrl.split("?")[0]; // remove SAS query params
  };

  const handleIntroductiveVideoUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    id: number
  ) => {
    setLoading(true);

    try {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!ALLOWED_TYPES.includes(file.type)) {
        setError("Only .mp4 video files are allowed.");
        return;
      }

      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setError("File size must be ≤ 100MB.");
        return;
      }

      setError(null);

      // Upload to Azure Blob
      const uploadedUrl = await uploadVideoToBlob(file);

      // Save to DB with the uploaded URL
      await handleSave(id, uploadedUrl);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (index: number, videoUrl: string) => {
    if (!dataList || !Array.isArray(dataList) || dataList.length === 0) {
      console.error("Error: dataList is empty");
      return;
    }
    setLoading(true);

    const selectedEntry = dataList.find((item) => item.id === index);
    if (!selectedEntry) {
      console.error("No entry found for id:", index);
      return;
    }
    const username = getStorageItemLocal("username") ?? "";
    const updatedEntry = {
      ...selectedEntry,
      isActive: selectedEntry.isActive,
      uploadBy: username,
      introvideoURL: videoUrl,
    };

    const updatedList = dataList.map((item) =>
      item.id === index ? updatedEntry : item
    );
    setDataList(updatedList);
    const url = `${appConfig.serviceUrls.getActiveState}/${index}`;

    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedEntry),
      });

      if (!response.ok) throw new Error("Failed to save data");

      alert("Data saved successfully");
      fetchData(); // Refresh the data if needed
    } catch (error) {
      console.error("Error updating data:", error);
      alert("Failed to save data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="container-wraper">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          paddingBottom: "5px",
        }}
      >
        <Typography className="bold-header-text">
          <b>State Introductive Video</b>{" "}
        </Typography>
        {error && <Typography className="bold-header-text">{error}</Typography>}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 3,
          marginBottom: 2,
        }}
      >
        {loading ? (
          <>
            {" "}
            <Spinner></Spinner>
          </>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              marginTop: 2,
              width: "100%",
            }}
          >
            {dataList?.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow className="custom-table-head-row">
                    <TableCell>
                      <b>State</b>
                    </TableCell>

                    <TableCell className="nowrap">
                      <b>Introductive Video </b>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dataList
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((item, index) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.name}</TableCell>

                        <TableCell>
                          {" "}
                          <Box sx={{ marginTop: "5px" }}>
                            <Box
                              className="gridItem"
                              sx={{ width: "120px", height: "120px" }}
                            >
                              {!item.introvideoURL ? ( //introvideoURL
                                <IconButton
                                  className="centerButton"
                                  component="label"
                                >
                                  <img
                                    src={frameadd}
                                    alt="upload"
                                    className="custom-icon"
                                  />

                                  {/* This automatically opens file dialog */}
                                  <input
                                    type="file"
                                    hidden
                                    accept="video/mp4"
                                    onChange={(e) =>
                                      handleIntroductiveVideoUpload(e, item.id)
                                    }
                                  />
                                </IconButton>
                              ) : (
                                <>
                                  <Box className="centeredContent">
                                    <img
                                      src={video1}
                                      alt="visible"
                                      className="custom-gallery"
                                    />
                                  </Box>
                                  <IconButton
                                    className="editButton"
                                    onClick={() =>
                                      handleViewIntroVideo(item.id)
                                    }
                                  >
                                    <img
                                      src={visiable}
                                      alt="view"
                                      className="icon"
                                    />
                                  </IconButton>

                                  <IconButton
                                    className="deleteButton"
                                    onClick={() => handleDelete(item.id)}
                                  >
                                    <img
                                      src={delete1}
                                      alt="delete"
                                      className="icon"
                                    />
                                  </IconButton>
                                </>
                              )}
                            </Box>
                            <Box sx={{ paddingBottom: "10px" }} />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            ) : (
              <Box sx={{ padding: "20px" }}>
                <Typography>No Data Found</Typography>
              </Box>
            )}
          </TableContainer>
        )}
      </Box>{" "}
      {openVideoDialog1 && (
        <VideoViewDialog
          open={openVideoDialog1}
          videoLink={videoPreview}
          onClose={() => setOpenVideoDialog1(false)}
        />
      )}
    </Box>
  );
};

export default StateIntroductiveVideo;
